import { useState, useEffect, useCallback } from 'react';
import type { Spot, SpotCategory } from '@ridehub/shared';
import { DEFAULT_SEARCH_RADIUS_METERS } from '@ridehub/shared';
import { apiClient } from '../../lib/api-client.js';

type SortOrder = 'distance' | 'likes' | 'checkins' | 'rank';

interface BBox {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface SpotSearchFilters {
  lat: number | null;
  lng: number | null;
  radius?: number;
  bbox?: BBox;
  category?: SpotCategory;
  tags?: string[];
  sort?: SortOrder;
}

export function useSpotSearch(filters: SpotSearchFilters) {
  const [spots, setSpots] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async () => {
    if (filters.lat === null || filters.lng === null) return;

    setLoading(true);
    setError(null);
    try {
      const baseParams: Record<string, string> = {
        lat: filters.lat.toString(),
        lng: filters.lng.toString(),
        ...(filters.category && { category: filters.category }),
        ...(filters.tags?.length && { tags: filters.tags.join(',') }),
        ...(filters.sort && { sort: filters.sort }),
      };
      // bbox が渡された場合はbbox検索、なければradius検索
      if (filters.bbox) {
        baseParams.minLat = filters.bbox.minLat.toString();
        baseParams.maxLat = filters.bbox.maxLat.toString();
        baseParams.minLng = filters.bbox.minLng.toString();
        baseParams.maxLng = filters.bbox.maxLng.toString();
      } else {
        baseParams.radius = (filters.radius ?? DEFAULT_SEARCH_RADIUS_METERS).toString();
      }
      const params = new URLSearchParams(baseParams);
      const data = await apiClient.get<Spot[]>(`/spots?${params}`);
      setSpots(data ?? []);
    } catch {
      setError('スポットの検索に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [
    filters.lat,
    filters.lng,
    filters.radius,
    filters.bbox?.minLat,
    filters.bbox?.maxLat,
    filters.bbox?.minLng,
    filters.bbox?.maxLng,
    filters.category,
    filters.tags,
    filters.sort,
  ]);

  useEffect(() => {
    search();
  }, [search]);

  return { spots, loading, error, refetch: search };
}
